import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiEdit2, FiTrash2, FiRefreshCw, FiClock, FiUser, FiDollarSign, FiCalendar, FiExternalLink, FiMail, FiPhone, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import CircularProgress from "@mui/material/CircularProgress";
import { remindersApi } from "./remindersApi";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { formatDate } from "../Common Components/CommonFunction";
import "./Reminders.css";

const STATUS_CONFIG = {
    ACTIVE: { label: "Active", class: "success" },
    EXPIRING_SOON: { label: "Expiring Soon", class: "warning" },
    EXPIRED: { label: "Expired", class: "danger" },
    RENEWED: { label: "Renewed", class: "info" },
    CANCELLED: { label: "Cancelled", class: "muted" },
};

const PRIORITY_CONFIG = {
    CRITICAL: { label: "Critical", class: "danger" },
    HIGH: { label: "High", class: "warning" },
    MEDIUM: { label: "Medium", class: "info" },
    LOW: { label: "Low", class: "success" },
};

const formatCurrency = (val) => {
    if (!val && val !== 0) return "-";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);
};

const getDaysLabel = (days) => {
    if (days === null || days === undefined) return "-";
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return "Expires today";
    if (days === 1) return "1 day left";
    return `${days} days left`;
};

const ReminderDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [item, setItem] = useState(null);
    const [showRenewForm, setShowRenewForm] = useState(false);
    const [renewing, setRenewing] = useState(false);
    const [renewForm, setRenewForm] = useState({
        new_expiry_date: "",
        renewed_date: new Date().toISOString().split("T")[0],
        amount_paid: "",
        payment_reference: "",
        payment_method: "",
        notes: "",
    });

    const fetchItem = useCallback(async () => {
        try {
            setLoading(true);
            const res = await remindersApi.getItem(id);
            if (res.status === "success") setItem(res.data);
        } catch (err) {
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchItem(); }, [fetchItem]);

    const handleDelete = async () => {
        if (!window.confirm(`Delete reminder "${item.title}"?`)) return;
        try {
            const res = await remindersApi.deleteItem(id);
            if (res.status === "success") {
                showSuccessToast("Reminder deleted");
                navigate("/reminders/list");
            }
        } catch (err) {
            showErrorToast(err.message);
        }
    };

    const handleRenew = async (e) => {
        e.preventDefault();
        if (!renewForm.new_expiry_date) {
            showErrorToast("New expiry date is required");
            return;
        }
        try {
            setRenewing(true);
            const payload = {
                reminder_id: id,
                ...renewForm,
                amount_paid: renewForm.amount_paid || null,
            };
            const res = await remindersApi.recordRenewal(payload);
            if (res.status === "success") {
                showSuccessToast("Renewal recorded successfully");
                setShowRenewForm(false);
                setRenewForm({ new_expiry_date: "", renewed_date: new Date().toISOString().split("T")[0], amount_paid: "", payment_reference: "", payment_method: "", notes: "" });
                fetchItem();
            }
        } catch (err) {
            showErrorToast(err.message);
        } finally {
            setRenewing(false);
        }
    };

    if (loading) {
        return <div className="rem-loading"><CircularProgress size={40} sx={{ color: "var(--accent)" }} /></div>;
    }

    if (!item) {
        return (
            <div className="rem-page">
                <div className="rem-empty">
                    <h3>Reminder not found</h3>
                    <button className="rem-btn rem-btn--primary" onClick={() => navigate("/reminders/list")}>Back to list</button>
                </div>
            </div>
        );
    }

    const sc = STATUS_CONFIG[item.status] || { label: item.status, class: "muted" };
    const pc = PRIORITY_CONFIG[item.priority] || { label: item.priority, class: "muted" };

    return (
        <div className="rem-page">
            <div className="rem-header">
                <div className="rem-header__left">
                    <button className="rem-btn rem-btn--ghost" onClick={() => navigate("/reminders/list")}>
                        <FiArrowLeft size={16} />
                    </button>
                    <div>
                        <div className="rem-detail__title-row">
                            <h1 className="rem-title">{item.title}</h1>
                            <span className={`rem-badge rem-badge--${sc.class} rem-badge--lg`}>{sc.label}</span>
                            <span className={`rem-badge rem-badge--${pc.class} rem-badge--lg`}>{pc.label}</span>
                        </div>
                        <p className="rem-subtitle">{item.reminder_code} | {item.category_name}</p>
                    </div>
                </div>
                <div className="rem-header-actions">
                    <button className="rem-btn rem-btn--success" onClick={() => setShowRenewForm(!showRenewForm)}>
                        <FiRefreshCw size={14} /> Renew
                    </button>
                    <button className="rem-btn rem-btn--outline" onClick={() => navigate(`/reminders/edit/${id}`)}>
                        <FiEdit2 size={14} /> Edit
                    </button>
                    <button className="rem-btn rem-btn--danger" onClick={handleDelete}>
                        <FiTrash2 size={14} /> Delete
                    </button>
                </div>
            </div>

            {/* Renewal Form (collapsible) */}
            {showRenewForm && (
                <div className="rem-card rem-renew-form">
                    <h3 className="rem-card__title"><FiRefreshCw size={16} /> Record Renewal</h3>
                    <form onSubmit={handleRenew} className="rem-form__grid">
                        <div className="rem-form__field">
                            <label>New Expiry Date <span className="rem-required">*</span></label>
                            <input type="date" value={renewForm.new_expiry_date} onChange={(e) => setRenewForm((f) => ({ ...f, new_expiry_date: e.target.value }))} className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Renewal Date</label>
                            <input type="date" value={renewForm.renewed_date} onChange={(e) => setRenewForm((f) => ({ ...f, renewed_date: e.target.value }))} className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Amount Paid</label>
                            <input type="number" value={renewForm.amount_paid} onChange={(e) => setRenewForm((f) => ({ ...f, amount_paid: e.target.value }))} placeholder="0.00" className="rem-input" step="0.01" />
                        </div>
                        <div className="rem-form__field">
                            <label>Payment Reference</label>
                            <input type="text" value={renewForm.payment_reference} onChange={(e) => setRenewForm((f) => ({ ...f, payment_reference: e.target.value }))} placeholder="Transaction ID" className="rem-input" />
                        </div>
                        <div className="rem-form__field">
                            <label>Payment Method</label>
                            <select value={renewForm.payment_method} onChange={(e) => setRenewForm((f) => ({ ...f, payment_method: e.target.value }))} className="rem-select">
                                <option value="">Select method</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                                <option value="Credit Card">Credit Card</option>
                                <option value="UPI">UPI</option>
                                <option value="Cash">Cash</option>
                                <option value="Cheque">Cheque</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="rem-form__field">
                            <label>Notes</label>
                            <input type="text" value={renewForm.notes} onChange={(e) => setRenewForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Renewal notes" className="rem-input" />
                        </div>
                        <div className="rem-form__field rem-form__field--full" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button type="button" className="rem-btn rem-btn--outline" onClick={() => setShowRenewForm(false)}>Cancel</button>
                            <button type="submit" className="rem-btn rem-btn--primary" disabled={renewing}>
                                {renewing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Record Renewal"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Detail Content */}
            <div className="rem-detail-grid">
                {/* Left: Details */}
                <div className="rem-card">
                    <h3 className="rem-card__title">Details</h3>
                    <div className="rem-detail__fields">
                        <div className="rem-detail__row">
                            <FiCalendar size={14} />
                            <span className="rem-detail__label">Expiry Date</span>
                            <span className="rem-detail__value rem-detail__value--bold">{formatDate(item.expiry_date)}</span>
                        </div>
                        <div className="rem-detail__row">
                            <FiClock size={14} />
                            <span className="rem-detail__label">Days Remaining</span>
                            <span className={`rem-detail__value ${item.days_until_expiry < 0 ? "rem-text--danger" : item.days_until_expiry <= 7 ? "rem-text--warning" : "rem-text--success"}`}>
                                {getDaysLabel(item.days_until_expiry)}
                            </span>
                        </div>
                        <div className="rem-detail__row">
                            <FiCalendar size={14} />
                            <span className="rem-detail__label">Start Date</span>
                            <span className="rem-detail__value">{formatDate(item.start_date)}</span>
                        </div>
                        {item.grace_period_days > 0 && (
                            <div className="rem-detail__row">
                                <FiClock size={14} />
                                <span className="rem-detail__label">Grace Period</span>
                                <span className="rem-detail__value">{item.grace_period_days} days</span>
                            </div>
                        )}
                        <div className="rem-detail__row">
                            <FiDollarSign size={14} />
                            <span className="rem-detail__label">Amount</span>
                            <span className="rem-detail__value rem-detail__value--bold">{formatCurrency(item.amount)}</span>
                        </div>
                        <div className="rem-detail__row">
                            <FiRefreshCw size={14} />
                            <span className="rem-detail__label">Recurrence</span>
                            <span className="rem-detail__value">{item.recurrence === "NONE" ? "One-time" : item.recurrence.replace("_", " ")}</span>
                        </div>
                        {item.reference_number && (
                            <div className="rem-detail__row">
                                <FiCheckCircle size={14} />
                                <span className="rem-detail__label">Reference</span>
                                <span className="rem-detail__value">{item.reference_number}</span>
                            </div>
                        )}
                        {item.assigned_to_name && (
                            <div className="rem-detail__row">
                                <FiUser size={14} />
                                <span className="rem-detail__label">Assigned To</span>
                                <span className="rem-detail__value">{item.assigned_to_name}</span>
                            </div>
                        )}
                    </div>
                    {item.description && (
                        <div className="rem-detail__desc">
                            <h4>Description</h4>
                            <p>{item.description}</p>
                        </div>
                    )}
                    {item.notes && (
                        <div className="rem-detail__desc">
                            <h4>Notes</h4>
                            <p>{item.notes}</p>
                        </div>
                    )}
                </div>

                {/* Right: Provider + Alert Config */}
                <div className="rem-detail-right">
                    {(item.provider_name || item.provider_email || item.provider_contact) && (
                        <div className="rem-card">
                            <h3 className="rem-card__title">Provider / Vendor</h3>
                            <div className="rem-detail__fields">
                                {item.provider_name && (
                                    <div className="rem-detail__row">
                                        <FiUser size={14} />
                                        <span className="rem-detail__label">Name</span>
                                        <span className="rem-detail__value">{item.provider_name}</span>
                                    </div>
                                )}
                                {item.provider_email && (
                                    <div className="rem-detail__row">
                                        <FiMail size={14} />
                                        <span className="rem-detail__label">Email</span>
                                        <span className="rem-detail__value">{item.provider_email}</span>
                                    </div>
                                )}
                                {item.provider_contact && (
                                    <div className="rem-detail__row">
                                        <FiPhone size={14} />
                                        <span className="rem-detail__label">Contact</span>
                                        <span className="rem-detail__value">{item.provider_contact}</span>
                                    </div>
                                )}
                                {item.provider_url && (
                                    <div className="rem-detail__row">
                                        <FiExternalLink size={14} />
                                        <span className="rem-detail__label">Website</span>
                                        <a href={item.provider_url} target="_blank" rel="noreferrer" className="rem-detail__link">{item.provider_url}</a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="rem-card">
                        <h3 className="rem-card__title"><FiAlertCircle size={16} /> Alert Schedule</h3>
                        <div className="rem-alert-tags">
                            {(item.alert_days_before || []).sort((a, b) => b - a).map((d) => (
                                <span key={d} className="rem-alert-tag">{d} days before</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Renewal History Timeline */}
            <div className="rem-card rem-card--full">
                <h3 className="rem-card__title"><FiRefreshCw size={16} /> Renewal History</h3>
                {(!item.renewals || item.renewals.length === 0) ? (
                    <div className="rem-empty-list">No renewal history yet</div>
                ) : (
                    <div className="rem-timeline">
                        {item.renewals.map((r, i) => (
                            <div key={r.id} className="rem-timeline__item">
                                <div className="rem-timeline__dot" />
                                <div className="rem-timeline__content">
                                    <div className="rem-timeline__header">
                                        <span className="rem-timeline__date">{formatDate(r.renewed_date)}</span>
                                        {r.amount_paid && <span className="rem-timeline__amount">{formatCurrency(r.amount_paid)}</span>}
                                    </div>
                                    <div className="rem-timeline__body">
                                        <span>{formatDate(r.previous_expiry_date)}</span>
                                        <span className="rem-timeline__arrow">&rarr;</span>
                                        <span className="rem-text--success">{formatDate(r.new_expiry_date)}</span>
                                    </div>
                                    <div className="rem-timeline__meta">
                                        {r.renewed_by && <span>By: {r.renewed_by}</span>}
                                        {r.payment_method && <span>Via: {r.payment_method}</span>}
                                        {r.payment_reference && <span>Ref: {r.payment_reference}</span>}
                                    </div>
                                    {r.notes && <div className="rem-timeline__notes">{r.notes}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReminderDetailPage;
